// directives/vAutoContrast.ts
import type { Directive } from 'vue'

export const vAutoContrast: Directive = {
  mounted(el: HTMLElement, binding) {
    applyContrast(el, binding.value)
  },
  
  updated(el: HTMLElement, binding) {
    if (binding.value !== binding.oldValue) {
      applyContrast(el, binding.value)
    }
  }
}

function applyContrast(el: HTMLElement, bgColor?: string) {
  const color = bgColor || window.getComputedStyle(el).backgroundColor
  const [r, g, b] = parseColor(color)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  
  el.style.color = luminance > 0.5 ? '#000000' : '#ffffff'
}

function parseColor(color: string): [number, number, number] {
  // HEX
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16)
    ]
  }
  
  // RGB/RGBA
  if (color.startsWith('rgb')) {
    const [r, g, b] = color.match(/[\d.]+/g)?.map(Number) || [0, 0, 0]
    return [r, g, b]
  }
  
  // OKLCH
  if (color.startsWith('oklch')) {
    const match = color.match(/oklch\(([\d.]+%?)\s+([\d.]+)\s+([\d.]+)/)
    if (match) {
      const l = parseFloat(match[1]) / (match[1].includes('%') ? 100 : 1)
      const c = parseFloat(match[2])
      const h = parseFloat(match[3])
      return oklchToRgb(l, c, h)
    }
  }
  
  // HSL/HSLA
  if (color.startsWith('hsl')) {
    const [h, s, l] = color.match(/[\d.]+/g)?.map(Number) || [0, 0, 0]
    return hslToRgb(h, s, l)
  }
  
  return [0, 0, 0]
}

function oklchToRgb(l: number, c: number, h: number): [number, number, number] {
  // OKLCH → OKLab
  const hRad = (h * Math.PI) / 180
  const a = c * Math.cos(hRad)
  const b = c * Math.sin(hRad)
  
  // OKLab → Linear RGB
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b
  
  const l3 = l_ ** 3
  const m3 = m_ ** 3
  const s3 = s_ ** 3
  
  const r = +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3
  const g = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3
  const bl = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.7076147010 * s3
  
  // Linear RGB → sRGB
  return [r, g, bl].map(v => {
    const abs = Math.abs(v)
    const sign = v < 0 ? -1 : 1
    const srgb = abs <= 0.0031308 
      ? v * 12.92 
      : sign * (1.055 * abs ** (1/2.4) - 0.055)
    return Math.max(0, Math.min(255, Math.round(srgb * 255)))
  }) as [number, number, number]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100
  l /= 100
  
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  
  let r = 0, g = 0, b = 0
  
  if (h < 60) { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else { r = c; b = x }
  
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ]
}